/**
 * Vue Router routes для модуля Storehouse
 * 
 * Экспортируем роуты для подключения в основном проекте
 * Layout компоненты импортируются из основного проекта через @/
 */

import moduleConnectionGuard from './middlewares/moduleConnectionGuard';
import moduleSettingGuard from './middlewares/moduleSettingGuard';

// Storehouse routes - используют стандартный Authenticated layout
export const storehouseRoutes = {
    path: '/storehouse',
    component: () => import('@/layouts/Authenticated.vue'),
        meta: { 
            requiresAuth: true,
            moduleParam: 'id', // все дочерние :id — id модуля
            title: 'Складской учёт'
    },
    children: [
    {
            path: ':id',
            name: 'StorehouseIndex',
            component: () => import('./pages/storehouse/Index.vue'),
            props: true
        },
        {
            path: ':id/add-operation',
        name: 'AddOperation',
        // Диспетчер: по флагу config.storehouse.use_new_add_operation рендерит либо старую (V1), либо новую (V2) версию формы
        component: () => import('./pages/storehouse/AddOperationDispatcher.vue'),
        props: true,
        meta: {
            title: 'Добавить операцию'
        }
    },
        {
            path: ':id/otk-control/:receiptInvoiceId',
        name: 'OtkControlPage',
        component: () => import('./pages/storehouse/OtkControlPage.vue'),
        props: (route) => ({ ...route.params, mode: 'control' }),
        meta: { 
            title: 'Контроль ОТК'
        }
    },
        {
            path: ':id/otk-defect/:receiptInvoiceId',
        name: 'OtkDefectPage',
        component: () => import('./pages/storehouse/OtkControlPage.vue'),
        props: (route) => ({ ...route.params, mode: 'defect' }),
        // Раздел открывается только там, где включена настройка «Работа с контролем ОТК (брак)»:
        // на других складах позиции нет и все действия по браку заведомо невыполнимы
        beforeEnter: moduleSettingGuard('with_otk_defect', {
            message: 'Работа с контролем ОТК (брак) на этом складе не подключена'
        }),
        meta: {
            title: 'Контроль ОТК (брак)'
        }
    }
    ]
};

// Receipt Invoice routes - Входной контроль приходных накладных
export const receiptInvoiceRoutes = {
    path: '/receipt-invoice-control',
    component: () => import('@/layouts/Authenticated.vue'),
    meta: { 
        requiresAuth: true,
        title: 'Входной контроль'
    },
    children: [
        {
            path: ':id',
            meta: { moduleParam: 'id' },
            name: 'ReceiptInvoiceControl',
            component: () => import('./pages/receiptInvoice/Index.vue'),
            props: true
        },
        {
            path: 'add-receipt-invoice',
            name: 'AddReceiptInvoice',
            component: () => import('./pages/receiptInvoice/AddReceiptInvoice.vue'),
            props: (route) => ({ moduleId: route.query.moduleId }),
            meta: { 
                title: 'Добавить накладную'
            }
        },
        {
            path: 'edit-receipt-invoice/:id',
            name: 'EditReceiptInvoice',
            component: () => import('./pages/receiptInvoice/AddReceiptInvoice.vue'),
            props: (route) => ({
                id: route.params.id,
                moduleId: route.query.moduleId || route.params.moduleId,
            }),
            meta: { 
                title: 'Редактировать накладную'
            }
        },
        {
            path: 'view-receipt-invoice/:id',
            name: 'ViewReceiptInvoice',
            component: () => import('./pages/receiptInvoice/ViewReceiptInvoice.vue'),
            props: (route) => ({
                id: route.params.id,
                moduleId: route.query.moduleId,
            }),
            meta: { 
                title: 'Просмотр накладной'
            }
        },
        {
            path: 'control-receipt-invoice/:id',
            name: 'IndexControlReceiptInvoice',
            component: () => import('./pages/receiptInvoice/IndexControlReceiptInvoice.vue'),
            props: (route) => ({
                id: route.params.id,
                moduleId: route.query.moduleId,
            }),
            meta: { 
                title: 'Контроль накладной'
            }
        },
        {
            path: 'control-receipt-invoice/:receiptInvoiceId/product/:productId',
            name: 'ControlReceiptInvoice',
            component: () => import('./pages/receiptInvoice/ControlReceiptInvoice.vue'),
            props: (route) => ({
                receiptInvoiceId: route.params.receiptInvoiceId,
                productId: route.params.productId,
                moduleId: route.query.moduleId,
            }),
            meta: { 
                title: 'Контроль продукции'
            }
        },
        {
            path: 'distribution/:id',
            name: 'ReceiptInvoiceDistribution',
            component: () => import('./pages/receiptInvoice/ReceiptInvoiceDistribution.vue'),
            props: (route) => ({
                id: route.params.id,
                moduleId: route.query.moduleId,
            }),
            meta: { 
                title: 'Распределение накладной'
            }
        }
    ]
};

// TSD Scan routes - используют мобильный WorkPlaceLayout
export const tsdScanRoutes = {
    path: '/tsd-scan',
    component: () => import('./layouts/WorkPlaceLayoutMobile.vue'),
    meta: { 
        requiresAuth: true,
        moduleParam: 'id', // все дочерние :id — id модуля
        title: 'ТСД'
    },
    // Родительский роут: загружаем связи без редиректа
    beforeEnter: moduleConnectionGuard(['storehouse', 'picking-desk'], { redirectOnMissing: false }),
    children: [
        {
            path: ':id',
            name: 'TsdScan',
            component: () => import('./pages/tsdScan/TsdScanIndex.vue'),
            props: true
        },
        {
            path: ':id/cell',
            name: 'TsdCell',
            component: () => import('./pages/tsdScan/TsdCellPage.vue'),
            props: true
        },
        {
            path: ':id/reception',
            name: 'TsdReception',
            component: () => import('./pages/tsdScan/TsdReceptionPage.vue'),
            props: true
        },
        {
            path: ':id/data-collection',
            name: 'TsdDataCollection',
            component: () => import('./pages/tsdScan/TsdDataCollectionPage.vue'),
            props: true
        },
        {
            path: ':id/picking',
            name: 'TsdTasks',
            component: () => import('./pages/tsdScan/TsdTasksPage.vue'),
            props: true
        },
        {
            path: ':id/tasks',
            name: 'TsdMovementTasks',
            component: () => import('./pages/tsdScan/TsdMovementTasksPage.vue'),
            props: true
        },
        {
            path: ':id/tasks/:taskId',
            name: 'TsdMovementTask',
            component: () => import('./pages/tsdScan/TsdMovementTaskPage.vue'),
            props: route => ({
                id: route.params.id,
                taskId: route.params.taskId
            })
        },
        {
            path: ':id/picking/:workshopPostUnitId/task/:taskId',
            name: 'TsdPickingTask',
            component: () => import('./pages/tsdScan/TsdPickingTask.vue'),
            props: route => ({
                moduleId: route.params.id,
                workshopPostUnitId: route.params.workshopPostUnitId,
                taskId: route.params.taskId
            })
        },
        {
            path: ':id/inventory',
            name: 'TsdInventory',
            component: () => import('./pages/tsdScan/TsdInventoryPage.vue'),
            props: true
        },
        {
            path: ':id/inventory/create',
            name: 'TsdInventoryCreate',
            component: () => import('./pages/tsdScan/TsdInventoryCreatePage.vue'),
            props: true
        },
        {
            path: ':id/inventory/:documentId',
            name: 'TsdInventoryDocument',
            component: () => import('./pages/tsdScan/TsdInventoryDocumentPage.vue'),
            props: route => ({
                id: route.params.id,
                documentId: route.params.documentId
            })
        },
        {
            path: ':id/inventory/:documentId/report',
            name: 'TsdInventoryReport',
            component: () => import('./pages/tsdScan/TsdInventoryReportPage.vue'),
            props: route => ({
                id: route.params.id,
                documentId: route.params.documentId
            })
        }
    ]
};

// Worker Master routes - используют мобильный WorkPlaceLayout
export const workerMasterRoutes = {
    path: '/worker-master-workplace',
    component: () => import('./layouts/WorkPlaceLayoutMobile.vue'),
    meta: { 
        requiresAuth: true,
        moduleParam: 'id', // все дочерние :id — id модуля
        title: 'Мастер/Рабочий'
    },
    // Родительский роут: загружаем связи без редиректа
    beforeEnter: moduleConnectionGuard(['storehouse'], { redirectOnMissing: false }),
    children: [
        {
            path: ':id',
            name: 'WorkerMasterIndex',
            component: () => import('./pages/workerMaster/WorkerMasterIndex.vue'),
            props: true
        },
        {
            path: ':id/cell-selection',
            name: 'CellSelection',
            component: () => import('./pages/workerMaster/CellSelection.vue'),
            props: true,
            // Дочерний роут: проверяем и редиректим если нет связи
            beforeEnter: moduleConnectionGuard(['storehouse'], { redirectOnMissing: true }),
            meta: { 
                title: 'Выбор ячейки',
                requiresConnection: 'storehouse'
            }
        },
        {
            path: ':id/cell/:cellId',
            name: 'CellPage',
            component: () => import('./pages/workerMaster/CellPage.vue'),
            props: true,
            // Дочерний роут: проверяем и редиректим если нет связи
            beforeEnter: moduleConnectionGuard(['storehouse'], { redirectOnMissing: true }),
            meta: { 
                title: 'Ячейка склада',
                requiresConnection: 'storehouse'
            }
        },
        {
            path: ':id/cell/:cellId/history',
            name: 'CellHistory',
            component: () => import('./pages/workerMaster/CellHistory.vue'),
            props: true,
            // Дочерний роут: проверяем и редиректим если нет связи
            beforeEnter: moduleConnectionGuard(['storehouse'], { redirectOnMissing: true }),
            meta: { 
                title: 'История ячейки',
                roles: ['Мастер', 'Администратор'],
                requiresConnection: 'storehouse'
            }
        },
        {
            path: ':id/storehouse-product/:storehouseProductId',
            name: 'StorehouseProductPage',
            component: () => import('./pages/workerMaster/StorehouseProductPage.vue'),
            props: true,
            // Дочерний роут: проверяем и редиректим если нет связи
            beforeEnter: moduleConnectionGuard(['storehouse'], { redirectOnMissing: true }),
            meta: { 
                title: 'Продукция на складе',
                requiresConnection: 'storehouse'
            }
        }
    ]
};

// Экспорт всех роутов вместе (как массив объектов)
export const allRoutes = [
    storehouseRoutes,
    workerMasterRoutes,
    receiptInvoiceRoutes,
    tsdScanRoutes
];

// Экспорт middleware для использования в основном проекте
export { moduleConnectionGuard };

export default allRoutes;

